import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Users, Building2, FileText, Activity, Settings, LogOut,
  ChevronRight, TrendingUp, DollarSign, Clock, AlertCircle, BarChart3, Webhook,
  Fingerprint, Palette
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { LogoFull } from '@/components/Logo';

const AdminDashboard = () => {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsData, usersData, logsData] = await Promise.all([
          api.getAnalytics(),
          hasPermission('manage_brokers') ? api.getUsers() : Promise.resolve([]),
          hasPermission('view_analytics') ? api.getAuditLogs(20) : Promise.resolve([])
        ]);
        setAnalytics(analyticsData);
        setUsers(usersData);
        setAuditLogs(logsData);
      } catch (error) {
        console.error('Error fetching admin data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [hasPermission]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const roleColors = {
    master_admin: 'bg-purple-100 text-purple-800',
    carrier_admin: 'bg-blue-100 text-blue-800',
    mga_admin: 'bg-emerald-100 text-emerald-800',
    broker: 'bg-amber-100 text-amber-800',
    agent: 'bg-orange-100 text-orange-800',
    user: 'bg-slate-100 text-slate-800'
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: Activity, permission: 'view_analytics' },
    { id: 'users', label: 'Users', icon: Users, permission: 'manage_brokers' },
    { id: 'organizations', label: 'Organizations', icon: Building2, permission: 'manage_mgas' },
    { id: 'policies', label: 'All Policies', icon: FileText, permission: 'view_analytics' },
    { id: 'audit', label: 'Audit Logs', icon: Clock, permission: 'view_analytics' },
    { id: 'analytics', label: 'Advanced Analytics', icon: BarChart3, permission: 'view_analytics', link: '/admin/analytics' },
    { id: 'claims', label: 'Claims', icon: AlertCircle, permission: 'view_analytics', link: '/claims' },
    { id: 'leads', label: 'Leads & Funnel', icon: TrendingUp, permission: 'view_analytics', link: '/admin/leads' },
    { id: 'kyc', label: 'KYC/AML Review', icon: Fingerprint, permission: 'view_analytics', link: '/admin/kyc' },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook, permission: 'manage_products', link: '/admin/webhooks' },
    { id: 'branding', label: 'White-Label', icon: Palette, permission: 'manage_products', link: '/admin/branding' },
    { id: 'carriers', label: 'Carrier APIs', icon: Building2, permission: 'manage_products', link: '/carriers' },
    { id: 'settings', label: 'Settings', icon: Settings, permission: 'view_analytics' }
  ].filter(item => hasPermission(item.permission));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="h-16 bg-white border-b border-[#DCE4ED] flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <LogoFull size="small" />
          </Link>
          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
            Admin Portal
          </span>
        </div>

        <div className="flex items-center gap-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleColors[user?.role] || roleColors.user}`}>
            {user?.role?.replace('_', ' ').toUpperCase()}
          </span>
          <span className="text-sm text-[#3D5A80]">{user?.full_name}</span>
          <Button variant="ghost" size="icon" onClick={handleLogout}>
            <LogOut size={18} />
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-[#DCE4ED] min-h-[calc(100vh-4rem)] p-4">
          <nav className="space-y-1">
            {sidebarItems.map((item) => (
              item.link ? (
                <Link
                  key={item.id}
                  to={item.link}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors text-[#3D5A80] hover:bg-[#F0F4F8]"
                  data-testid={`nav-${item.id}`}
                >
                  <item.icon size={20} />
                  {item.label}
                  <ChevronRight size={14} className="ml-auto text-[#8DA0B9]" />
                </Link>
              ) : (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === item.id 
                    ? 'bg-[#1A3A64]/10 text-[#1A3A64] font-medium' 
                    : 'text-[#3D5A80] hover:bg-[#F0F4F8]'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon size={20} />
                {item.label}
              </button>
              )
            ))}
          </nav>

          <div className="mt-8 pt-4 border-t border-[#DCE4ED]">
            <Link to="/builder">
              <Button className="w-full" variant="outline">
                Go to Builder
                <ChevronRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          {activeTab === 'overview' && analytics && (
            <div className="space-y-8">
              <div>
                <h1 className="text-2xl font-semibold text-[#0D1B2A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Dashboard Overview
                </h1>
                <p className="text-[#3D5A80]">Platform metrics and recent activity</p>
              </div>

              {/* Stats Cards */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl border border-[#DCE4ED] p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#1A3A64]/10 rounded-xl flex items-center justify-center">
                      <Users size={24} className="text-[#1A3A64]" />
                    </div>
                  </div>
                  <p className="text-3xl font-semibold text-[#0D1B2A]">{analytics.total_users}</p>
                  <p className="text-sm text-[#8DA0B9]">Total Users</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-xl border border-[#DCE4ED] p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#00AEEF]/10 rounded-xl flex items-center justify-center">
                      <FileText size={24} className="text-[#00AEEF]" />
                    </div>
                  </div>
                  <p className="text-3xl font-semibold text-[#0D1B2A]">{analytics.total_policies}</p>
                  <p className="text-sm text-[#8DA0B9]">Total Policies</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl border border-[#DCE4ED] p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#F59E0B]/10 rounded-xl flex items-center justify-center">
                      <TrendingUp size={24} className="text-[#F59E0B]" />
                    </div>
                  </div>
                  <p className="text-3xl font-semibold text-[#0D1B2A]">{analytics.active_policies}</p>
                  <p className="text-sm text-[#8DA0B9]">Active Policies</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl border border-[#DCE4ED] p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#1A3A64]/10 rounded-xl flex items-center justify-center">
                      <DollarSign size={24} className="text-[#1A3A64]" />
                    </div>
                  </div>
                  <p className="text-3xl font-semibold text-[#0D1B2A]">
                    ${analytics.total_monthly_premium?.toLocaleString() || 0}
                  </p>
                  <p className="text-sm text-[#8DA0B9]">Monthly Premium</p>
                </motion.div>
              </div>

              {/* Recent Policies */}
              <div className="bg-white rounded-xl border border-[#DCE4ED] p-6">
                <h2 className="text-lg font-semibold text-[#0D1B2A] mb-4">Recent Policies</h2>
                <div className="space-y-3">
                  {analytics.recent_policies?.map((policy) => (
                    <div key={policy.policy_id} className="flex items-center justify-between p-3 bg-[#F0F4F8] rounded-lg">
                      <div>
                        <p className="font-medium text-[#0D1B2A]">{policy.name}</p>
                        <p className="text-sm text-[#8DA0B9]">{policy.policy_id}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          policy.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                          policy.status === 'draft' ? 'bg-amber-100 text-amber-800' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {policy.status}
                        </span>
                        <p className="text-sm text-[#1A3A64] font-medium mt-1">
                          ${policy.total_monthly_premium}/mo
                        </p>
                      </div>
                    </div>
                  ))}
                  {(!analytics.recent_policies || analytics.recent_policies.length === 0) && (
                    <p className="text-center text-[#8DA0B9] py-4">No recent policies</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    User Management
                  </h1>
                  <p className="text-[#3D5A80]">{users.length} users</p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-[#DCE4ED] overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#F0F4F8] border-b border-[#DCE4ED]">
                    <tr>
                      <th className="text-left px-6 py-3 text-sm font-medium text-[#3D5A80]">User</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-[#3D5A80]">Role</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-[#3D5A80]">Company</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-[#3D5A80]">2FA</th>
                      <th className="text-left px-6 py-3 text-sm font-medium text-[#3D5A80]">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-b border-[#DCE4ED] last:border-0">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-[#0D1B2A]">{u.full_name}</p>
                            <p className="text-sm text-[#8DA0B9]">{u.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${roleColors[u.role] || roleColors.user}`}>
                            {u.role?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[#3D5A80]">{u.company_name || '-'}</td>
                        <td className="px-6 py-4">
                          {u.two_factor_enabled ? (
                            <span className="text-emerald-600 text-sm">Enabled</span>
                          ) : (
                            <span className="text-[#8DA0B9] text-sm">Disabled</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-[#8DA0B9]">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Audit Logs
                </h1>
                <p className="text-[#3D5A80]">Security and activity monitoring</p>
              </div>

              <div className="bg-white rounded-xl border border-[#DCE4ED] p-6">
                <div className="space-y-3">
                  {auditLogs.map((log) => (
                    <div key={log.log_id} className="flex items-start gap-4 p-3 bg-[#F0F4F8] rounded-lg">
                      <div className="w-10 h-10 bg-[#00AEEF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Activity size={18} className="text-[#00AEEF]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#0D1B2A]">
                          {log.action} - {log.resource_type}
                        </p>
                        <p className="text-sm text-[#8DA0B9]">
                          User: {log.user_id} • {log.ip_address || 'Unknown IP'}
                        </p>
                      </div>
                      <span className="text-xs text-[#8DA0B9]">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                  ))}
                  {auditLogs.length === 0 && (
                    <p className="text-center text-[#8DA0B9] py-8">No audit logs yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h1 className="text-2xl font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Platform Settings
              </h1>
              <div className="bg-white rounded-xl border border-[#DCE4ED] p-6">
                <p className="text-[#8DA0B9]">Settings panel coming soon...</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
